/**
 * ComingSoonPage (Iter 9.47.o)
 *
 * Stealth-mode public landing. Minimal white canvas with:
 *   - Phygital wordmark + admin-editable tagline
 *   - <SignInModal /> launched via "Sign In" button (matches LoginPage layout)
 *   - <EarlyAccessModal /> launched via "Are you a service provider?" link
 *     (the canonical Provider Waitlist popup with brand name capture)
 *
 * Iter 9.47.o changes:
 *   - "Sign in / Join" → "Sign In" (single label per user spec).
 *   - Replaced placeholder InterestPopup with the canonical EarlyAccessModal
 *     so brand-name capture + city + 11-category multi-select all work and
 *     feed the existing /api/early-access pipeline. Brand name is what
 *     surfaces in the user's /me Hosting section while their category
 *     waits to launch.
 *   - Help widget is intentionally NOT rendered on this page (per user
 *     spec: no help/footer chrome on auth/coming-soon pages).
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import PhygitalIcon from "@/components/PhygitalIcon";
import { EditableText, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import SignInModal from "@/components/SignInModal";
import EarlyAccessModal from "@/components/EarlyAccessModal";

export default function ComingSoonPage() {
  const { editMode } = useAdmin();
  const [signInOpen, setSignInOpen] = useState(false);
  const [providerOpen, setProviderOpen] = useState(false);

  return (
    <div
      className="min-h-screen bg-white flex flex-col items-center justify-center px-6 relative overflow-hidden"
      data-testid="coming-soon-page"
    >
      {/* Subtle background gradient */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-gray-50 to-transparent rounded-full blur-3xl opacity-60" />
      </div>

      <div className="relative z-10 max-w-md w-full text-center">
        <div className="flex justify-center mb-8">
          <PhygitalIcon className="w-14 h-14" variant="dark" />
        </div>

        <EditableSection sectionId="coming-soon-hero" label="Hero Section" className={editMode ? "p-4" : ""}>
          <h1
            className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 tracking-tight"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="coming-soon-title"
          >
            <EditableText
              sectionId="coming-soon-hero"
              field="title"
              defaultValue="Phygital"
              className="inline-block"
            />
          </h1>

          <p className="text-base text-gray-500 mb-10 leading-relaxed" data-testid="coming-soon-tagline">
            <EditableText
              sectionId="coming-soon-hero"
              field="tagline"
              defaultValue="Your preferences. Your experience."
              className="inline-block"
            />
          </p>
        </EditableSection>

        {/* Single CTA: Sign In (popup). Returning users + new users use the
            same popup (email magic link, phone, or Google). */}
        <Button
          onClick={() => setSignInOpen(true)}
          className="bg-gray-900 text-white hover:bg-gray-800 h-12 px-10 rounded-full font-medium"
          data-testid="coming-soon-sign-in-btn"
        >
          Sign In
        </Button>

        {/* Service provider entry - opens the canonical EarlyAccessModal
            (Provider Waitlist with brand name + city + multi-category
            selection). Submission feeds /api/early-access, which surfaces
            the brand name in the user's /me Hosting section with a
            "waiting for category to launch in your city" status. */}
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setProviderOpen(true)}
            className="text-sm text-gray-500 hover:text-gray-800 underline underline-offset-4"
            data-testid="coming-soon-provider-link"
          >
            Are you a service provider? Join as a host
          </button>
        </div>

        <div className="mt-16 text-gray-300 text-xs">
          <p>
            <EditableText
              sectionId="coming-soon-footer"
              field="copyright"
              defaultValue="2026 Phygital. All rights reserved."
              className="inline-block"
            />
          </p>
        </div>
      </div>

      {/* Popups */}
      <SignInModal open={signInOpen} onOpenChange={setSignInOpen} />
      <EarlyAccessModal open={providerOpen} onOpenChange={setProviderOpen} />
    </div>
  );
}
